/**
 * RenderPreview
 * ---------------------------------------------------------------
 * Embeds the Remotion <Player> with the correct cinematic template
 * for an asset. Pure presentation — no DB writes. Knows how to
 * compute duration for both dialogue-driven templates and the new
 * Progression / Resonant artifact templates.
 */

import { Player } from "@remotion/player";
import { useMemo } from "react";
import type { SimulationContent } from "@/lib/contentGenerator";
import {
  planTimeline,
  selectTemplate,
  FPS,
  WIDTH,
  HEIGHT,
  progressionDurationFrames,
  resonantArtifactDurationFrames,
} from "./index";

interface Props {
  sim: SimulationContent;
  format: string;
  className?: string;
}

export const RenderPreview: React.FC<Props> = ({ sim, format, className }) => {
  const { key, Component } = selectTemplate(format);

  const totalFrames = useMemo(() => {
    if (key === "progression") {
      // Default to 3 stages if archetype mapping yields none
      return progressionDurationFrames(3);
    }
    if (key === "resonant") {
      return resonantArtifactDurationFrames();
    }
    return planTimeline(sim).totalFrames;
  }, [key, sim]);

  return (
    <div className={className} style={{ aspectRatio: "9/16", maxWidth: 360, width: "100%" }}>
      <Player
        component={Component}
        inputProps={{ sim }}
        durationInFrames={totalFrames}
        compositionWidth={WIDTH}
        compositionHeight={HEIGHT}
        fps={FPS}
        controls
        loop
        clickToPlay
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 6,
          overflow: "hidden",
          border: "1px solid rgba(120, 200, 220, 0.14)",
          background: "#020306",
        }}
      />
    </div>
  );
};
